import { useEffect, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@majourneys/icons";
import { PW } from "./pewter-shell";

/**
 * A tiny toast system (lab #09): one message at a time, bottom centre, above
 * the phone's bottom bar. Any code can call `showToast`; one `<Toaster />`
 * mounted on the page shows it. A new toast replaces the one on screen.
 *
 * The store lives at module level, so a call from a click handler needs no
 * provider and no prop drilling, and the auto-dismiss timer lives here too,
 * which keeps it testable without rendering anything.
 */
export interface ToastInput {
  message: string;
  /** A gold text button after the message, e.g. "Undo". */
  actionLabel?: string;
  onAction?: () => void;
  /** How long it stays, in ms. */
  duration?: number;
}

export interface ToastItem extends ToastInput {
  id: number;
}

export const TOAST_DURATION_MS = 3200;
/** The leave animation; the toast stays in the DOM this long after closing. */
export const TOAST_LEAVE_MS = 150;

let current: ToastItem | null = null;
let nextId = 1;
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function clearTimer() {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

export function showToast(input: ToastInput): number {
  clearTimer();
  const id = nextId++;
  current = { ...input, id };
  timer = setTimeout(() => dismissToast(id), input.duration ?? TOAST_DURATION_MS);
  emit();
  return id;
}

/** Close the toast on screen; with an id, only if that toast is still the one shown. */
export function dismissToast(id?: number): void {
  if (!current || (id !== undefined && current.id !== id)) return;
  clearTimer();
  current = null;
  emit();
}

export function subscribeToasts(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getToast(): ToastItem | null {
  return current;
}

/* Transform and opacity only. Reduced motion keeps the fade and drops the
   rise. The leave is quicker than the enter, so a dismissed toast gets out of
   the way. */
const TOAST_CSS = `
@keyframes mjToastIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes mjToastOut { from { opacity: 1; transform: none; } to { opacity: 0; transform: translateY(6px); } }
@keyframes mjToastFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes mjToastFadeOut { from { opacity: 1; } to { opacity: 0; } }
.mj-toaster { bottom: calc(96px + env(safe-area-inset-bottom, 0px)); }
@media (min-width: 1024px) { .mj-toaster { bottom: 32px; } }
.mj-toast { animation: mjToastIn 220ms cubic-bezier(0.23, 1, 0.32, 1) both; }
.mj-toast[data-leaving="true"] { animation: mjToastOut ${TOAST_LEAVE_MS}ms ease-in both; }
@media (prefers-reduced-motion: reduce) {
  .mj-toast { animation-name: mjToastFadeIn; }
  .mj-toast[data-leaving="true"] { animation-name: mjToastFadeOut; }
}
.mj-toast-action:hover { text-decoration: underline; text-underline-offset: 2px; }
`;

/**
 * Shows the current toast. Mount once per page. Dark surfaces take no band,
 * so the toast is a flat navy pill with a gold check.
 */
export function Toaster() {
  const toast = useSyncExternalStore(subscribeToasts, getToast, () => null);
  const [mounted, setMounted] = useState(false);
  // The last toast shown, kept on screen while it plays its leave animation.
  const [shown, setShown] = useState<ToastItem | null>(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (toast) {
      setShown(toast);
      return;
    }
    const t = setTimeout(() => setShown(null), TOAST_LEAVE_MS);
    return () => clearTimeout(t);
  }, [toast]);

  if (!mounted || typeof document === "undefined") return null;

  const item = toast ?? shown;
  const leaving = !toast && !!shown;

  return createPortal(
    <div
      className="mj-toaster"
      style={{
        position: "fixed",
        insetInline: 0,
        zIndex: "var(--z-toast, 60)" as unknown as number,
        display: "flex",
        justifyContent: "center",
        padding: "0 16px",
        pointerEvents: "none",
      }}
    >
      <style>{TOAST_CSS}</style>
      {/* The live region stays mounted, so a screen reader hears each new
          message rather than missing the first one. */}
      <div role="status" aria-live="polite" aria-atomic="true">
        {item && (
          <div
            key={item.id}
            className="mj-toast"
            data-leaving={leaving ? "true" : undefined}
            data-testid="toast"
            style={{
              pointerEvents: "auto",
              display: "flex",
              alignItems: "center",
              gap: 10,
              maxWidth: 420,
              borderRadius: 999,
              padding: "8px 16px",
              background: PW.navyGradient,
              color: "#fff",
              fontSize: 13,
              lineHeight: "18px",
              boxShadow: "0 12px 30px -10px rgba(17,14,57,0.55), inset 0 1px 0 rgba(255,255,255,0.16)",
            }}
          >
            <Icon name="ri-check-line" className="shrink-0 text-[16px]" color={PW.gold} aria-hidden />
            <span style={{ minWidth: 0 }}>{item.message}</span>
            {item.actionLabel && item.onAction && (
              <button
                type="button"
                className="mj-toast-action"
                onClick={() => {
                  item.onAction?.();
                  dismissToast(item.id);
                }}
                style={{
                  minHeight: 24,
                  minWidth: 24,
                  marginInlineStart: 2,
                  padding: "2px 4px",
                  border: 0,
                  background: "transparent",
                  color: PW.gold,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {item.actionLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
